/**
 * 66.7 lean step 2 (the owner: *"I think the partial lean should work, I did see some designs for them as well"*): a person
 * leaning out of cover — lane E's `edge` and `corner` poses, out — has its eyes, its gun and its upper body aside of where
 * it stands, by the pose's measured lean (COVER_POSE_SIZE[pose].out: `aside` at the eyes' height `eye`, person units — a
 * person is 1.8 tall), toward the side it holds (CoverHold.side: +1 to the right of its heading). A lean pivots at the
 * feet: a point h up the body moves aside × min(1, h / the pose's eye height). Read by the eye (fineTerrain.ts
 * personEyePoint) and the gun (bullets.ts muzzlePoint). The body a round meets stays at its spot (the sphere, or decision
 * 132's capsules) and the lean adds what shows past the cover's edge: the head and the gun's shoulder (`leanPart`,
 * COVER_POSE_SIZE[pose].out.part), which is also where a shooter aims (hitboxCenter). (The first cut moved the whole
 * body aside by height: past the edge a still shooter's rounds landed at every height, a third of them on the legs.) Low cover rises over it (its
 * stance) and a crest slides up (prone), so neither leans; a unit not out of cover — the field absent — is as it was.
 */
import type { PlayerState, Vec3 } from '../types';
import { COVER_POSE_SIZE } from '../constants/action';
import { PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecScale, vecSub, vecDot, vecNormalize, vecLength, vecCross, surfaceTangentFrame } from '../math';

/**
 * How far (world) and which way a point `height` (world units) up a person moves while it leans out of cover; null when it
 * does not lean (no cover held, down behind it, a pose that does not lean, in the air or a seat, or no scale given).
 */
export function leanOffset(p: Pick<PlayerState, 'cover' | 'heading' | 'surfaceDir' | 'airborne' | 'seatPos'>, k: number | undefined, height: number): Vec3 | null {
  const c = p.cover;
  if (!c || !c.out || !c.side || (c.pose !== 'edge' && c.pose !== 'corner') || p.airborne || p.seatPos || k === undefined) return null;
  const size = COVER_POSE_SIZE[c.pose].out;
  const unit = (PERSON_BODY_HEIGHT * k) / 1.8;   // person units → world
  const n = vecNormalize(p.surfaceDir);
  const along = p.heading ? vecSub(p.heading, vecScale(n, vecDot(p.heading, n))) : surfaceTangentFrame(n).tx;
  const heading = vecLength(along) > 1e-12 ? vecNormalize(along) : surfaceTangentFrame(n).tx;
  const right = vecNormalize(vecCross(heading, n));
  const share = Math.max(0, Math.min(1, height / (size.eye * unit)));
  return vecScale(right, c.side * size.aside * unit * share);
}

/**
 * What a person leaning out of cover shows past its edge (66.7): the head and the gun's shoulder, a capsule from the
 * pose's `part.low` to `part.high` up the body (each end aside by the lean at its height), radius `part.r`; `feet` is
 * the point under the person (its ground), `n` its up. Null when it does not lean.
 */
export function leanPart(p: Pick<PlayerState, 'cover' | 'heading' | 'surfaceDir' | 'airborne' | 'seatPos'>, k: number | undefined, feet: Vec3, n: Vec3): { p0: Vec3; p1: Vec3; r: number } | null {
  if (k === undefined) return null;
  const lo = leanOffset(p, k, 0);
  if (!lo) return null;
  const part = COVER_POSE_SIZE[p.cover!.pose as 'edge' | 'corner'].out.part;
  const unit = (PERSON_BODY_HEIGHT * k) / 1.8;
  const end = (h: number): Vec3 => vecAdd(vecAdd(feet, vecScale(n, h * unit)), leanOffset(p, k, h * unit)!);
  return { p0: end(part.low), p1: end(part.high), r: part.r * unit };
}

/** How far (person heights) a pose's lean reaches aside at the eyes — for a bot judging whether a lean clears its cover. */
export function leanReachPeople(pose: 'edge' | 'corner'): number {
  return COVER_POSE_SIZE[pose].out.aside / 1.8;
}
